/**
 * The set of policy types an offer is quoting, normalised so two offers can be compared.
 *
 * Deduped and sorted, so option order and repeats never make two views of the same quote look
 * like different products. Used wherever "is this the same quote, or a different product?" has to
 * be answered consistently (#34790):
 *
 * - staff offer search builds its dedupe key from it, so a customer's concurrent FarmHome (Z) and
 *   FarmRental (Y) quotes stay separately visible while revisions of one quote still collapse;
 * - the staff newer-offer redirect refuses to jump between offers whose sets differ, on both the
 *   staff-fe Meilisearch path and the staff-appsync-be fallback.
 *
 * It lives here rather than being repeated at each call site because those answers must agree:
 * a rule enforced in one place and not another is how the redirect came to throw agents from
 * their Home quote onto the customer's Rental quote in the first place.
 */
export type OfferProductSetOption = { type?: string | null } | null | undefined;

export function offerProductSet(options: OfferProductSetOption[] | null | undefined): string[] {
  if (!Array.isArray(options)) {
    return [];
  }
  const types = options.map((option) => option?.type).filter((type): type is string => Boolean(type));
  return [...new Set(types)].sort();
}

/** True when both offers are quoting exactly the same set of policy types. */
export function isSameOfferProductSet(
  a: OfferProductSetOption[] | null | undefined,
  b: OfferProductSetOption[] | null | undefined
): boolean {
  const left = offerProductSet(a);
  const right = offerProductSet(b);
  return left.length === right.length && left.every((type, index) => type === right[index]);
}
